import { AgentsIcon, Loader2Icon, PlusIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";

import {
  cachedAgents,
  createAgent,
  fetchAgentRuns,
  fetchAgents,
  invalidateAgents,
  setAgentKeepWorking,
  setAgentStatus,
  type Agent,
  type AgentAutonomy,
  type AgentRun,
} from "../../morrowAgents";
import { useMorrowSignal } from "../../hooks/useMorrowSignal";
import { agentActivityLine, newestRunByAgent, type AgentActivityTone } from "./agentActivity";
import { withMockAgents, isMockAgentId } from "./agentsMock";
import { isAgentsMockMode } from "./agentsMockMode";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { useI18n, type MessageKey } from "~/i18n";
import { MorrowWorkspacePage } from "./MorrowWorkspacePage";
import { AgentAvatar, PRESET_MASCOTS } from "./AgentAvatar";
import { AgentTabs, joinCharter, useConnectedApps, type AgentFormValues } from "./agentFormBits";

function agentStatusKey(status: Agent["status"]): MessageKey {
  switch (status) {
    case "paused":
      return "agents.workspace.status.paused";
    case "archived":
      return "agents.workspace.status.archived";
    default:
      return "agents.workspace.status.active";
  }
}

function autonomyKey(autonomy: AgentAutonomy): MessageKey {
  switch (autonomy) {
    case "read":
      return "workflows.new.autonomy.read";
    case "autonomous":
      return "workflows.new.autonomy.autonomous";
    default:
      return "workflows.new.autonomy.approval";
  }
}

function statusVariant(status: Agent["status"]) {
  switch (status) {
    case "active":
      return "success" as const;
    case "paused":
      return "warning" as const;
    case "archived":
      return "outline" as const;
    default:
      return "secondary" as const;
  }
}

const ACTIVITY_TONE_CLASS: Record<AgentActivityTone, string> = {
  working: "text-emerald-600 dark:text-emerald-400",
  scheduled: "text-muted-foreground",
  problem: "text-amber-600 dark:text-amber-500",
  idle: "text-muted-foreground",
};

/**
 * One line saying what this agent is doing — working, when it wakes next, or how its last run
 * ended. The card used to show only `status`, which says whether the agent is switched on and
 * nothing about whether it is getting anything done.
 */
function AgentActivityLine({
  agent,
  newestRun,
}: {
  readonly agent: Agent;
  readonly newestRun: AgentRun | undefined;
}) {
  const activity = agentActivityLine({ agent, newestRun, nowMs: Date.now() });
  return (
    <div className={`mt-2 flex items-center gap-1.5 text-xs ${ACTIVITY_TONE_CLASS[activity.tone]}`}>
      {activity.tone === "working" ? (
        <Loader2Icon className="size-3 animate-spin" aria-hidden />
      ) : null}
      <span className="truncate">{activity.label}</span>
    </div>
  );
}

/** The agents workspace list — real agents from the API, plus a create dialog. */
export function MorrowAgentsListPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { agent: agentFromSearch, mock: mockParam } = useSearch({ strict: false }) as {
    agent?: string;
    mock?: string;
  };
  const agentsMockMode = isAgentsMockMode({ mock: mockParam });

  // Seed from the last-good cache so re-entering Agents shows instantly (no flash);
  const [agents, setAgents] = useState<readonly Agent[]>(() =>
    withMockAgents(cachedAgents() ?? [], agentsMockMode),
  );
  const [loading, setLoading] = useState(() => cachedAgents() === null);
  // The run log, only so each card can say what the agent is actually doing. The agents
  // endpoint carries status and next-wake but nothing about runs.
  const [runs, setRuns] = useState<readonly AgentRun[]>([]);
  const newestRuns = useMemo(() => newestRunByAgent(runs), [runs]);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Create-dialog form state — the same tabbed AgentFormValues the details modal edits.
  const [form, setForm] = useState<AgentFormValues>(() => ({
    avatar: PRESET_MASCOTS[0]!,
    name: "",
    role: "",
    soul: "",
    purpose: "",
    autonomy: "approval",
    allowedApps: [],
    // Strict opt-in: a NEW agent starts with NOTHING granted. The owner switches on
    // each ability (and picks apps) deliberately — nothing is on until explicitly given.
    caps: { cloudBrowser: false, browser: false, vault: false, memoryWrite: false, email: false },
    keepWorking: false,
    dailyBudgetMinutes: 0,
  }));
  const patchForm = useCallback(
    (p: Partial<AgentFormValues>) => setForm((f) => ({ ...f, ...p })),
    [],
  );
  const connectedApps = useConnectedApps(creating);

  const load = useCallback(async () => {
    // Revalidate silently — no setLoading(true), so a background refresh never
    // flashes the spinner over already-rendered (cached) content.
    const [rows, runRows] = await Promise.all([fetchAgents(), fetchAgentRuns()]);
    setAgents(rows);
    setRuns(runRows);
    setLoading(false);
  }, []);

  // Agents act while nobody is looking, so this page cannot be fetch-once-on-mount: a
  // scheduled wake would run to completion and nothing here would change until a reload.
  // The server pokes `agents` whenever a run opens or settles or a status flips; drop the
  // read-through cache first so the refetch actually hits the API.
  useMorrowSignal("agents", () => {
    invalidateAgents();
    void load();
  });

  // Pause/resume an agent from the list. Optimistic flip, then persist + refresh.
  const toggleStatus = useCallback(
    async (a: Agent) => {
      const next = a.status === "paused" ? "active" : "paused";
      setAgents((prev) => prev.map((x) => (x.id === a.id ? { ...x, status: next } : x)));
      await setAgentStatus(a.id, next);
      await load();
    },
    [load],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Deep link: /agents?agent=<id> opens that agent's window (back-compat with the
  // sidebar's agent search results).
  useEffect(() => {
    if (agentFromSearch) {
      void navigate({
        to: "/agents/$agentId",
        params: { agentId: agentFromSearch },
        replace: true,
      });
    }
  }, [agentFromSearch, navigate]);

  const canCreate = form.name.trim().length > 0;

  const submit = useCallback(async () => {
    if (!canCreate || saving) return;
    setSaving(true);
    const created = await createAgent({
      name: form.name.trim(),
      role: form.role.trim(),
      avatar: form.avatar || null,
      instruction: joinCharter(form.soul, form.purpose),
      autonomy: form.autonomy,
      // Strict opt-in: empty = NO apps (send []), not null. `[]` = "none" to the
      // backend gate; null/undefined would mean "all connected apps" — the opposite.
      allowedApps: [...form.allowedApps],
      capabilities: form.caps,
      ...(form.dailyBudgetMinutes > 0
        ? { budget: { dailyActiveSeconds: form.dailyBudgetMinutes * 60 } }
        : {}),
      status: "active",
    });
    setSaving(false);
    if (created) {
      // "Keep working" is a separate opt-in write (arms the loop server-side); only when the
      // owner turned it on at creation. Best-effort — never blocks opening the new agent.
      if (form.keepWorking) await setAgentKeepWorking(created.id, true);
      setCreating(false);
      setForm({
        avatar: PRESET_MASCOTS[0]!,
        name: "",
        role: "",
        soul: "",
        purpose: "",
        autonomy: "approval",
        allowedApps: [],
        // Strict opt-in: a NEW agent starts with NOTHING granted. The owner switches on
        // each ability (and picks apps) deliberately — nothing is on until explicitly given.
        caps: {
          cloudBrowser: false,
          browser: false,
          vault: false,
          memoryWrite: false,
          email: false,
        },
        keepWorking: false,
        dailyBudgetMinutes: 0,
      });
      // Warm the cache so the new agent is already present when its window mounts
      // (avoids a post-create "Agent not found" flash), then open it.
      await fetchAgents();
      void navigate({ to: "/agents/$agentId", params: { agentId: created.id } });
    }
  }, [canCreate, saving, form, navigate]);

  const body = useMemo(() => {
    if (loading) {
      return (
        <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" />
          <span className="text-sm">{t("agents.workspace.loading")}</span>
        </div>
      );
    }
    if (agents.length === 0) {
      return (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border py-16 text-center">
          <AgentsIcon className="size-8 text-muted-foreground" />
          <div className="text-sm font-medium text-foreground">
            {t("agents.workspace.empty.title")}
          </div>
          <div className="max-w-sm text-sm text-muted-foreground">
            {t("agents.workspace.empty.description")}
          </div>
          <Button onClick={() => setCreating(true)}>
            <PlusIcon className="size-4" /> {t("agents.workspace.newAgent")}
          </Button>
        </div>
      );
    }
    return (
      <div className="@container">
        <ul className="grid grid-cols-1 gap-3 @[22rem]:grid-cols-2 @[40rem]:grid-cols-3">
          {agents.map((a) => (
            <li key={a.id} className="min-w-0">
              <Card
                className="h-full cursor-pointer p-4 text-left transition hover:bg-accent/30"
                render={
                  // Real router Link (not an onClick button) so cmd/ctrl/middle-click,
                  // right-click, and hover-href all work — the intermittent "click doesn't
                  // open" was modifier-clicks no-op'ing on a plain button.
                  <Link to="/agents/$agentId" params={{ agentId: a.id }} />
                }
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <AgentAvatar name={a.name} seed={a.id} avatar={a.avatar} size={36} round />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 truncate text-sm font-semibold text-foreground">
                        <span className="truncate">{a.name}</span>
                        {agentsMockMode && isMockAgentId(a.id) ? (
                          <Badge variant="outline" className="shrink-0 px-1.5 py-0 text-[10px]">
                            {t("agents.workspace.demo")}
                          </Badge>
                        ) : null}
                      </div>
                      {a.role ? (
                        <div className="truncate text-xs text-muted-foreground">{a.role}</div>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {a.status === "active" || a.status === "paused" ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={(e) => {
                          // inside the card <Link>: don't navigate when toggling
                          e.preventDefault();
                          e.stopPropagation();
                          void toggleStatus(a);
                        }}
                      >
                        {a.status === "paused"
                          ? t("agents.workspace.resume")
                          : t("agents.workspace.pause")}
                      </Button>
                    ) : null}
                    <Badge variant={statusVariant(a.status)} size="sm">
                      {t(agentStatusKey(a.status))}
                    </Badge>
                  </div>
                </div>
                {a.instruction ? (
                  <p className="mt-3 line-clamp-2 text-xs text-muted-foreground">{a.instruction}</p>
                ) : null}
                <AgentActivityLine agent={a} newestRun={newestRuns.get(a.id)} />
                <div className="mt-3 flex flex-wrap items-center gap-1">
                  <Badge variant="outline" size="sm">
                    {t(autonomyKey(a.autonomy))}
                  </Badge>
                  {a.keepWorking === 1 ? (
                    <Badge
                      variant="outline"
                      size="sm"
                      className="border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
                    >
                      ● Keep working
                    </Badge>
                  ) : null}
                  {a.tags.map((tag) => (
                    <Badge key={`${tag.handle}:${tag.channelScope}`} variant="secondary" size="sm">
                      @{tag.handle}
                    </Badge>
                  ))}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      </div>
    );
  }, [loading, agents, navigate, agentsMockMode, t, toggleStatus]);

  return (
    <MorrowWorkspacePage
      title={t("agents.title")}
      subtitle={t("agents.workspace.subtitle")}
      icon={<AgentsIcon />}
      wide
      action={
        <Button onClick={() => setCreating(true)}>
          <PlusIcon className="size-4" /> {t("agents.workspace.newAgent")}
        </Button>
      }
    >
      {body}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogPopup>
          <DialogHeader>
            <DialogTitle>{t("agents.workspace.create.title")}</DialogTitle>
            <DialogDescription>{t("agents.workspace.create.description")}</DialogDescription>
          </DialogHeader>
          <DialogPanel>
            <div className="flex max-h-[62vh] min-h-[440px] flex-col">
              <AgentTabs values={form} patch={patchForm} connected={connectedApps} />
            </div>
          </DialogPanel>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)} disabled={saving}>
              {t("activities.action.cancel")}
            </Button>
            <Button onClick={() => void submit()} disabled={!canCreate || saving}>
              {saving ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : (
                <PlusIcon className="size-4" />
              )}
              {t("agents.workspace.createAgent")}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>
    </MorrowWorkspacePage>
  );
}
